import type { RouteLocationRaw, Router } from 'vue-router';
import type { QueryChanges } from '../types/urlQuery';

let unsaved: QueryChanges = {};
let navigations = 0;
let isHistoryMoving = false;
let isWritingQuery = false;
let isRouterTracked = false;

export function useUrlQuery() {
  const router = useRouter();

  async function writeUnsaved(): Promise<void> {
    if (navigations || isHistoryMoving) return;

    const { query } = router.currentRoute.value;

    unsaved = Object.fromEntries(
      Object.entries(unsaved).filter(([key, value]) => query[key] !== value),
    );

    const changes = { ...unsaved };

    if (!Object.keys(changes).length) return;

    isWritingQuery = true;

    const failure = await router
      .replace({ query: { ...query, ...changes } })
      .catch(() => true);

    isWritingQuery = false;

    if (!failure) await writeUnsaved();
  }

  function saveQuery(changes: QueryChanges): void {
    const { query } = router.currentRoute.value;

    for (const [key, value] of Object.entries(changes)) {
      const saved = key in unsaved ? unsaved[key] : query[key];

      if (saved !== value) unsaved[key] = value;
    }

    void writeUnsaved();
  }

  if (!isRouterTracked) {
    isRouterTracked = true;
    trackNavigations(router, writeUnsaved);
  }

  return { saveQuery };
}

function trackNavigations(router: Router, writeUnsaved: () => Promise<void>): void {
  const retryWrite = () => {
    if (!isWritingQuery) void writeUnsaved();
  };

  router.options.history.listen(() => {
    isHistoryMoving = true;
  });

  router.afterEach(() => {
    isHistoryMoving = false;
    retryWrite();
  });

  for (const method of ['push', 'replace'] as const) {
    const navigate = router[method].bind(router);

    router[method] = ((to: RouteLocationRaw) => {
      navigations += 1;

      return navigate(to).finally(() => {
        navigations -= 1;
        retryWrite();
      });
    }) as Router['push'];
  }
}
